import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import type { ThirdPartyLogoProps } from '@atlassian/logo-third-party/types';

// Legacy custom @atlaskit/icon glyphs render one size smaller than requested, so this container
// is bumped up a step to compensate (see RovoLogoFromSVG's `correctedSize`).
const legacyContainerStyles = {
	small: xcss({
		width: '16px',
		height: '16px',
	}),
	medium: xcss({
		width: '24px',
		height: '24px',
	}),
	large: xcss({
		width: '32px',
		height: '32px',
	}),
	//t25 tile icons do not get bumped up to 40/48px to avoid additional padding
	xlarge: xcss({
		width: '32px',
		height: '32px',
	}),
} as const;

// The official brand icon library (behind rovo-search-team-eu-26-brand-icon-library) renders
// through ADS Tile, whose size scale already matches this 1:1 — no compensation needed.
const accurateContainerStyles = {
	xxsmall: xcss({
		width: '16px',
		height: '16px',
	}),
	xsmall: xcss({
		width: '20px',
		height: '20px',
	}),
	small: xcss({
		width: '24px',
		height: '24px',
	}),
	medium: xcss({
		width: '32px',
		height: '32px',
	}),
	large: xcss({
		width: '40px',
		height: '40px',
	}),
} as const;

type LegacySize = 'small' | 'medium' | 'large' | 'xlarge';
type AccurateSize = 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large';

export interface SVGIconProps {
	glyph: (props?: Pick<ThirdPartyLogoProps, 'size'>) => JSX.Element;
	label?: string;
	legacySize: LegacySize;
	accurateSize: AccurateSize;
}

const iconStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

export const SVGIcon = ({
	glyph: SVG,
	label,
	legacySize,
	accurateSize,
}: SVGIconProps): React.JSX.Element => {
	const isAccurateSizeEnabled = fg('rovo-search-team-eu-26-brand-icon-library');
	return (
		<Box
			xcss={[
				isAccurateSizeEnabled
					? accurateContainerStyles[accurateSize]
					: legacyContainerStyles[legacySize],
				iconStyles,
			]}
			aria-label={label || undefined}
			role={label ? 'img' : 'presentation'}
		>
			{isAccurateSizeEnabled ? <SVG size={accurateSize} /> : <SVG />}
		</Box>
	);
};
